/**
 * Config-home mount health + pod-local fallback.
 *
 * In a claudespace pod, `~/.claude` is a per-user GCS FUSE mount (see
 * `.claude/rules/claude-session-persistence.md`). When the gcsfuse socket drops,
 * every fs op under it fails with `ENOTCONN: socket is not connected` — including
 * the transcript-dir `mkdir` the PTY harness runs before spawning the CLI and the
 * credentials write in `prepareEnvironment`. The turn dies with a raw ENOTCONN,
 * and because it isn't a retriable API error, re-sending to the agent just re-runs
 * against the still-dead mount: only a full pod recreate (which remounts gcsfuse)
 * recovers. Observed live as "Builder stuck: ENOTCONN error on mkdir, restart
 * doesn't fix it".
 *
 * This module detects that mount-disconnect class and self-heals by pointing
 * Claude's config home at a **pod-local** directory (`~/.claude-local`, on the
 * pod's real overlay disk — only `~/.claude` itself is the symlinked mount) via
 * `CLAUDE_CONFIG_DIR`. Since `claudeConfigHome()` is read by the transcript path,
 * the credentials path, onboarding (`claudeJsonPath`), and the spawned CLI's env
 * (`inheritedEnv` copies `process.env`), one flip cascades to our code and the
 * child CLI alike. Credentials + onboarding are re-synthesized every boot, so a
 * fresh pod-local config home is fully functional; the only degradation is that
 * cross-pod transcript persistence is lost for that session (a `--resume` miss is
 * already handled by the query executor's stale-session recovery). The agent keeps
 * working WITHOUT a pod restart.
 */

import { mkdir } from "node:fs/promises";
import { homedir } from "node:os";
import { join } from "node:path";
import { claudeConfigHome, projectSlug } from "./settings.js";

/** fs error codes a dead FUSE mount raises on any op under the mount point. */
const MOUNT_DISCONNECT_CODES = new Set(["ENOTCONN", "EIO", "ESTALE", "ENXIO"]);

const MOUNT_DISCONNECT_MESSAGES = [
  "socket is not connected",
  "transport endpoint is not connected",
];

/**
 * Does `err` look like the shared GCS FUSE mount having dropped its socket?
 * Matches on the fs error `code` (primary signal) or the message text (belt and
 * braces for wrappers that lose `code`).
 */
export function isMountDisconnectError(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const code = (err as { code?: unknown }).code;
  if (typeof code === "string" && MOUNT_DISCONNECT_CODES.has(code)) return true;
  const message = (err as { message?: unknown }).message;
  if (typeof message !== "string") return false;
  const lower = message.toLowerCase();
  return MOUNT_DISCONNECT_MESSAGES.some((needle) => lower.includes(needle));
}

/** Pod-local config home used when the shared `~/.claude` mount is unusable. */
export function podLocalConfigHome(): string {
  return join(homedir(), ".claude-local");
}

export interface ConfigHomeHealth {
  /** The config home in effect after the check (flipped to pod-local on fallback). */
  configHome: string;
  /** True when the shared mount was unusable and we fell back to pod-local. */
  fellBack: boolean;
}

type Logger = { warn(message: string, data?: Record<string, unknown>): void };

/**
 * Ensure the Claude config home is writable before a spawn. Write-probes the
 * transcript's `projects/<slug>` directory (the exact op that wedges on a dead
 * mount); on a mount-disconnect error, flips `CLAUDE_CONFIG_DIR` to a pod-local
 * dir and creates it there. Non-mount errors are real bugs and rethrow.
 *
 * Idempotent: once flipped, later calls probe the pod-local dir and report
 * `fellBack: false` (the env already points there and the probe succeeds).
 */
export async function ensureUsableClaudeConfigHome(
  cwd: string,
  log?: Logger,
): Promise<ConfigHomeHealth> {
  const configHome = claudeConfigHome();
  try {
    await mkdir(join(configHome, "projects", projectSlug(cwd)), { recursive: true });
    return { configHome, fellBack: false };
  } catch (err) {
    if (!isMountDisconnectError(err)) throw err;

    const fallback = podLocalConfigHome();
    log?.warn(
      "shared ~/.claude mount is unreachable; falling back to a pod-local config home. " +
        "Session history will not persist across pods until the mount recovers.",
      {
        code: (err as { code?: unknown }).code ?? null,
        from: configHome,
        to: fallback,
      },
    );
    // Cascades to our path helpers (transcript, credentials, onboarding) and to
    // the spawned CLI via inheritedEnv, which snapshots process.env at spawn.
    process.env.CLAUDE_CONFIG_DIR = fallback;
    await mkdir(join(fallback, "projects", projectSlug(cwd)), { recursive: true });
    return { configHome: fallback, fellBack: true };
  }
}
